import { type DataFunctionArgs, json } from '@remix-run/node'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { conform, useForm } from '@conform-to/react'
import { z } from 'zod'
import { redirectWithToast } from '~/utils/flash-session.server.ts'
import {
	useActionData,
	useNavigation,
	useFormAction,
	Form,
} from '@remix-run/react'
import { ErrorList, Field } from '~/components/forms.tsx'
import { StatusButton } from '~/components/ui/status-button.tsx'
import { sendEmail } from '~/utils/email.server.ts'
import { ContactUsEmail } from '~/routes/contact/contactemail.server.tsx'
import { Button } from '~/components/ui/button.tsx'
import { Separator } from '~/components/ui/separator.tsx'

const ACTION = '/resources/support-form'

const SupportFormSchema = z.object({
	name: z.string().min(2, 'Name must be at least 2 characters'),
	email: z.string().email('Invalid email address'),
	// prayer: z.string().min(5, 'Prayer must be at least 5 characters'),
})

export async function action({ request }: DataFunctionArgs) {
	const formData = await request.formData()
	const submission = await parse(formData, {
		schema: SupportFormSchema.superRefine(async (data, ctx) => {}),
		acceptMultipleErrors: () => true,
		async: true,
	})
	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}
	const { name, email } = submission.value

	const response = await sendEmail({
		to: 'info@studyworkpray.org',
		subject: `VIA NOVA - Prayer Support Form - ${name}`,
		react: (
			<ContactUsEmail
				email={email}
				name={name}
				message={'I would like to join the prayer team.'}
			/>
		),
	})

	// const session = await signup({ email, username, password, name })
	if (response.status === 'success') {
		return redirectWithToast('/', {
			title: 'Success!',
			description: 'Your message has been sent.',
		})
	} else {
		submission.error[''] = response.error.message
		return json({ status: 'error', submission } as const, { status: 500 })
	}
}

export default function SupportForm() {
	const actionData = useActionData<typeof action>()
	const navigation = useNavigation()
	const formAction = useFormAction()

	const [form, fields] = useForm({
		id: 'support',
		constraint: getFieldsetConstraint(SupportFormSchema),
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			return parse(formData, { schema: SupportFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col justify-center gap-1">
				<p>To go straight to our giving page, please click the button below.</p>
				<Button asChild size="wide" variant="default">
					<a
						target="_blank"
						href="https://vianova.stellarwebsystems.com/donations/pool/79bdb7d4-264e-11ee-9cac-16118fddfe69"
						rel="noreferrer"
					>
						Give
					</a>
				</Button>
			</div>
			<Separator className="my-2" />
			<p>
				If you would like to support us through prayer, we would love to add you
				to our prayer intentions mailing list. Please fill out your information
				below so we can keep you posted on our prayer intentions.
			</p>
			<Form
				method="POST"
				action={ACTION}
				className="min-w-[368px] max-w-[420px]"
				{...form.props}
			>
				<Field
					labelProps={{ htmlFor: fields.name.id, children: 'Name' }}
					inputProps={{
						...conform.input(fields.name),
						autoComplete: 'name',
						tabIndex: 1,
						// autoFocus:
						// 	typeof actionData === 'undefined' ||
						// 	typeof fields.name.initialError !== 'undefined',
					}}
					errors={fields.name.errors}
				/>
				<Field
					labelProps={{ htmlFor: fields.email.id, children: 'Email' }}
					inputProps={{
						...conform.input(fields.email),
						autoComplete: 'email',
						tabIndex: 2,
						// autoFocus:
						// 	typeof actionData === 'undefined' ||
						// 	typeof fields.email.initialError !== 'undefined',
					}}
					errors={fields.email.errors}
				/>
				{/* <TextareaField
					errors={fields.prayer.errors}
					labelProps={{ htmlFor: fields.prayer.id, children: 'Prayer' }}
					textareaProps={{
						'aria-rowspan': 5,
						tabIndex: 3,
						...conform.textarea(fields.prayer),
					}}
				/> */}
				<ErrorList errors={[...form.errors]} id={form.errorId} />

				<div className="flex items-center justify-between gap-6">
					<StatusButton
						className="w-full"
						size="wide"
						tabIndex={4}
						status={
							navigation.state === 'submitting' &&
							navigation.formAction === formAction &&
							navigation.formMethod === 'POST'
								? 'pending'
								: actionData?.status ?? 'idle'
						}
						type="submit"
						disabled={navigation.state !== 'idle'}
					>
						Join Via's Prayer Team
					</StatusButton>
				</div>
			</Form>
		</div>
	)
}
